/**
 * CompensationHistory — timeline of the candidate's compensation revisions,
 * a career-progression sparkline, an AI-reasoned market benchmark, and a
 * jump-justifier that flags aggressive desired-CTC deltas.
 *
 * Data flow:
 *   - GET /candidates/{cid}/compensation-history returns the rows +
 *     rolled-up `jump`, `last_increment`, `compensation_benchmark`.
 *   - POST /compensation-history to add manual rows.
 *   - POST /compensation-history/ai-seed to have Claude parse the resume.
 *   - POST /compensation-benchmark to (re)generate the market range.
 *
 * The chart is a hand-rolled SVG line (no chart lib dep) — plenty for
 * a small compensation timeline. Market P50 is overlaid as a dashed
 * horizontal band when the benchmark is available.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Plus, TrendingUp, DollarSign, RefreshCw, AlertTriangle, ArrowUpRight, Trash2 } from "lucide-react";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD", "CAD"];

export default function CompensationHistory({ candidateId }) {
    const [data, setData] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [benchBusy, setBenchBusy] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ date: "", title: "", company: "", ctc: "", currency: "INR", kind: "salary", note: "" });

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/compensation-history`);
            setData(data);
        } catch (err) {
            if (err?.response?.status === 403) {
                setData({ compensation_history: [], jump: { has_data: false }, forbidden: true });
                return;
            }
            toast.error(formatApiError(err));
        }
    };
    useEffect(() => { load(); }, [candidateId]);

    const aiSeed = async () => {
        setSeeding(true);
        try {
            const { data: r } = await api.post(`/candidates/${candidateId}/compensation-history/ai-seed`);
            toast.success(r.entry_count > 0 ? `Added ${r.entry_count} row${r.entry_count === 1 ? "" : "s"}` : (r.message || "No compensation signals detected"));
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSeeding(false); }
    };

    const runBenchmark = async () => {
        setBenchBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/compensation-benchmark`);
            toast.success("Market benchmark refreshed");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBenchBusy(false); }
    };

    const addRow = async () => {
        if (!form.date || !form.ctc) { toast.error("Date and CTC are required"); return; }
        try {
            await api.post(`/candidates/${candidateId}/compensation-history`, { ...form, ctc: parseFloat(form.ctc) });
            setShowAdd(false);
            setForm({ date: "", title: "", company: "", ctc: "", currency: "INR", kind: "salary", note: "" });
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeRow = async (eid) => {
        if (!window.confirm("Delete this entry?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/compensation-history/${eid}`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!data) return <div className="text-sm text-muted-foreground p-4">Loading history…</div>;
    if (data.forbidden) return null;
    const history = data.compensation_history || [];
    const bench = data.compensation_benchmark;
    const jump = data.jump || { has_data: false };
    const lastInc = data.last_increment;

    return (
        <section className="space-y-4" data-testid="compensation-history">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-bold text-lg flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Compensation history & intelligence
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        onClick={aiSeed}
                        disabled={seeding}
                        variant="outline"
                        className="rounded-none h-8 text-xs"
                        data-testid="ch-ai-seed-btn"
                    >
                        <Sparkles className={`w-3.5 h-3.5 mr-1 ${seeding ? "animate-pulse" : ""}`} />
                        {seeding ? "Reading resume…" : "AI parse from resume"}
                    </Button>
                    <Button
                        onClick={() => setShowAdd(true)}
                        className="rounded-none h-8 text-xs"
                        data-testid="ch-add-btn"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add row
                    </Button>
                </div>
            </div>

            {/* Jump justifier */}
            {jump.has_data && (
                <JumpJustifier jump={jump} lastIncrement={lastInc} benchmark={bench} />
            )}

            {/* Career progression chart */}
            {history.length > 0 && (
                <ProgressionChart history={history} benchmark={bench} />
            )}

            {/* History rows */}
            <div className="border border-border bg-white">
                <div className="px-4 py-2 border-b border-border bg-secondary/40 grid grid-cols-[110px_1fr_1fr_120px_110px_30px] gap-2 text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                    <span>Date</span><span>Role</span><span>Company</span><span>CTC</span><span>Kind</span><span></span>
                </div>
                {history.length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                        No history yet. Use <b>AI parse from resume</b> or <b>Add row</b> above.
                    </div>
                )}
                {[...history].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((h) => (
                    <div key={h.id} className="px-4 py-2 border-t border-border grid grid-cols-[110px_1fr_1fr_120px_110px_30px] gap-2 items-center hover:bg-secondary/30 text-sm" data-testid={`ch-row-${h.id}`}>
                        <span className="tabular-nums text-xs">{h.date}</span>
                        <span className="text-xs">{h.title || "—"}</span>
                        <span className="text-xs">{h.company || "—"}</span>
                        <span className="tabular-nums text-xs font-semibold">
                            {h.ctc != null ? new Intl.NumberFormat("en-IN").format(h.ctc) : "—"} <span className="text-muted-foreground font-normal">{h.currency || ""}</span>
                        </span>
                        <span className="text-[11px]">
                            <KindPill kind={h.kind} />
                            {h.confidence != null && h.confidence < 0.7 && (
                                <span className="ml-1 text-[9px] text-amber-800" title="Low AI confidence — please review">?</span>
                            )}
                        </span>
                        <button type="button" onClick={() => removeRow(h.id)} className="text-muted-foreground hover:text-rose-700" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Add row inline form */}
            {showAdd && (
                <div className="border border-ikb bg-ikb/5 p-3 space-y-2" data-testid="ch-add-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none h-9" data-testid="ch-add-date" />
                        <Input placeholder="Role" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none h-9" data-testid="ch-add-title" />
                        <Input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none h-9" data-testid="ch-add-company" />
                        <div className="flex gap-2">
                            <Input type="number" placeholder="CTC*" value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} className="rounded-none h-9 flex-1" data-testid="ch-add-ctc" />
                            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="h-9 px-2 border border-border rounded-none bg-white text-sm">
                                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="h-9 px-2 border border-border rounded-none bg-white text-sm">
                            <option value="salary">Base salary</option>
                            <option value="increment">Annual increment</option>
                            <option value="promotion">Promotion</option>
                            <option value="role_change">Role change</option>
                        </select>
                        <Input placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-none h-9" data-testid="ch-add-note" />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button onClick={() => setShowAdd(false)} variant="outline" className="rounded-none h-8 text-xs">Cancel</Button>
                        <Button onClick={addRow} className="rounded-none h-8 text-xs" data-testid="ch-add-save-btn">Save row</Button>
                    </div>
                </div>
            )}

            {/* Benchmark card */}
            <BenchmarkCard bench={bench} onGenerate={runBenchmark} busy={benchBusy} />
        </section>
    );
}

function KindPill({ kind }) {
    const map = {
        salary:      { label: "Base",      cls: "text-foreground/70 border-border bg-white" },
        increment:   { label: "Increment", cls: "text-emerald-800 border-emerald-300 bg-emerald-50" },
        promotion:   { label: "Promotion", cls: "text-violet-800 border-violet-300 bg-violet-50" },
        role_change: { label: "Role Δ",    cls: "text-blue-800 border-blue-300 bg-blue-50" },
    };
    const m = map[kind] || map.salary;
    return <span className={`inline-block px-1.5 py-0 text-[10px] border ${m.cls}`}>{m.label}</span>;
}

function JumpJustifier({ jump, lastIncrement, benchmark }) {
    const tierMeta = {
        aggressive: { cls: "border-rose-400 bg-rose-50 text-rose-900",       icon: AlertTriangle, label: "Aggressive jump" },
        high:       { cls: "border-amber-400 bg-amber-50 text-amber-900",   icon: AlertTriangle, label: "High jump" },
        healthy:    { cls: "border-emerald-400 bg-emerald-50 text-emerald-900", icon: ArrowUpRight, label: "Healthy jump" },
        modest:     { cls: "border-border bg-white text-foreground/80",      icon: ArrowUpRight, label: "Modest jump" },
        lateral:    { cls: "border-border bg-secondary/40 text-foreground/70", icon: ArrowUpRight, label: "Lateral move" },
    };
    const m = tierMeta[jump.tier] || tierMeta.modest;
    const Icon = m.icon;
    const fmt = (n) => n != null ? new Intl.NumberFormat("en-IN").format(Math.round(n)) : "—";
    return (
        <div className={`border p-3 ${m.cls}`} data-testid="ch-jump-justifier" data-tier={jump.tier}>
            <div className="flex items-center gap-2 flex-wrap">
                <Icon className="w-4 h-4" />
                <div className="text-sm font-semibold">{m.label}: {jump.jump_pct}%{jump.jump_pct_max !== jump.jump_pct && ` – ${jump.jump_pct_max}%`}</div>
                {jump.needs_justification && (
                    <span className="text-[10px] px-1.5 py-0 bg-white border border-current uppercase tracking-widest font-mono">needs justification</span>
                )}
            </div>
            <div className="text-xs mt-1">
                Current <b>{fmt(jump.current_ctc)}</b> → Desired <b>{fmt(jump.desired_min)}{jump.desired_max !== jump.desired_min && ` – ${fmt(jump.desired_max)}`}</b>
                {benchmark?.p50 && <> · Market P50 <b>{fmt(benchmark.p50)}</b> {benchmark.currency}</>}
            </div>
            {lastIncrement && (
                <div className="text-xs mt-1 opacity-80">
                    Last increment: <b>{lastIncrement.delta_pct}%</b> on {lastIncrement.date} · {lastIncrement.kind}
                </div>
            )}
        </div>
    );
}

function ProgressionChart({ history, benchmark }) {
    // Sort ascending by date so we plot left→right in time
    const rows = useMemo(() => [...history]
        .filter((h) => h.date && h.ctc != null)
        .sort((a, b) => a.date.localeCompare(b.date)), [history]);
    if (rows.length < 2) return null;

    const W = 600, H = 140, PAD = 32;
    const ctcs = rows.map((r) => r.ctc);
    let yMin = Math.min(...ctcs);
    let yMax = Math.max(...ctcs);
    if (benchmark?.p50) {
        yMin = Math.min(yMin, benchmark.p25 || benchmark.p50);
        yMax = Math.max(yMax, benchmark.p75 || benchmark.p50);
    }
    const yRange = Math.max(1, yMax - yMin);
    const xStep = (W - PAD * 2) / Math.max(1, rows.length - 1);
    const yFor = (v) => PAD + (H - PAD * 2) * (1 - (v - yMin) / yRange);
    const points = rows.map((r, i) => ({ x: PAD + i * xStep, y: yFor(r.ctc), r }));
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const fmt = (n) => new Intl.NumberFormat("en-IN", { notation: "compact" }).format(n);

    return (
        <div className="border border-border bg-white p-3" data-testid="ch-progression-chart">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Career progression</div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" role="img" aria-label="Compensation progression chart">
                {/* Market band */}
                {benchmark?.p25 && benchmark?.p75 && (
                    <rect
                        x={PAD} y={yFor(benchmark.p75)}
                        width={W - PAD * 2} height={Math.max(0, yFor(benchmark.p25) - yFor(benchmark.p75))}
                        fill="rgba(16,185,129,0.08)" stroke="rgba(16,185,129,0.35)" strokeDasharray="4,4"
                    />
                )}
                {benchmark?.p50 && (
                    <line x1={PAD} y1={yFor(benchmark.p50)} x2={W - PAD} y2={yFor(benchmark.p50)} stroke="rgba(16,185,129,0.6)" strokeDasharray="3,3" />
                )}
                {/* Line */}
                <path d={path} fill="none" stroke="var(--ikb, #1a56db)" strokeWidth="2" />
                {/* Points */}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={p.x} cy={p.y} r="4" fill="white" stroke="var(--ikb, #1a56db)" strokeWidth="2" />
                        <title>{p.r.date} · {p.r.company || ""} · {fmt(p.r.ctc)} {p.r.currency || ""}</title>
                    </g>
                ))}
                {/* Y-axis labels */}
                <text x="4" y={PAD + 4} fontSize="9" fill="#666" className="font-mono">{fmt(yMax)}</text>
                <text x="4" y={H - PAD + 4} fontSize="9" fill="#666" className="font-mono">{fmt(yMin)}</text>
            </svg>
            {benchmark?.p50 && (
                <div className="text-[10px] text-emerald-800 text-center mt-1">Dashed green line = market P50 · Green band = P25-P75</div>
            )}
        </div>
    );
}

function BenchmarkCard({ bench, onGenerate, busy }) {
    return (
        <div className="border border-emerald-200 bg-emerald-50/40" data-testid="ch-benchmark">
            <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-800" />
                    <h4 className="font-display font-bold text-base text-emerald-900">Market benchmark</h4>
                    <span className="text-[11px] text-emerald-800/70">AI reasoned estimate — not market data</span>
                </div>
                <Button onClick={onGenerate} disabled={busy} variant="outline" className="rounded-none border-emerald-300 h-8 text-xs" data-testid="ch-benchmark-btn">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
                    {bench ? "Regenerate" : "Generate benchmark"}
                </Button>
            </div>
            <div className="p-4">
                {!bench && !busy && (
                    <p className="text-sm text-foreground/70">
                        Click <b>Generate benchmark</b> to have AI estimate a P25/P50/P75 market range based on role, years, location, and skills.
                    </p>
                )}
                {bench && (
                    <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-3" data-testid="ch-benchmark-values">
                            <BenchmarkStat label="P25" value={bench.p25} currency={bench.currency} />
                            <BenchmarkStat label="P50 (median)" value={bench.p50} currency={bench.currency} highlight />
                            <BenchmarkStat label="P75" value={bench.p75} currency={bench.currency} />
                        </div>
                        {bench.verdict && (
                            <div className="text-sm mt-2">
                                <span className="text-muted-foreground">Verdict on desired CTC:</span>{" "}
                                <b className={verdictClass(bench.verdict)} data-testid="ch-benchmark-verdict">{prettyVerdict(bench.verdict)}</b>
                                {bench.vs_market_pct != null && <span className="text-muted-foreground ml-2">({bench.vs_market_pct > 0 ? "+" : ""}{bench.vs_market_pct}% vs P50)</span>}
                            </div>
                        )}
                        {bench.reasoning && (
                            <p className="text-sm text-foreground/80 mt-2 leading-relaxed" data-testid="ch-benchmark-reasoning">{bench.reasoning}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function BenchmarkStat({ label, value, currency, highlight }) {
    const fmt = (n) => n != null ? new Intl.NumberFormat("en-IN").format(Math.round(n)) : "—";
    return (
        <div className={`border ${highlight ? "border-emerald-500 bg-white" : "border-border bg-white"} p-2`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`tabular-nums font-display font-bold ${highlight ? "text-xl text-emerald-800" : "text-lg text-foreground/80"}`}>{fmt(value)}</div>
            <div className="text-[10px] text-muted-foreground">{currency}</div>
        </div>
    );
}

function prettyVerdict(v) {
    return ({ below_market: "Below market", market_aligned: "Market-aligned", above_market: "Above market", stretch: "Stretch" })[v] || v;
}
function verdictClass(v) {
    return ({ below_market: "text-blue-700", market_aligned: "text-emerald-700", above_market: "text-amber-700", stretch: "text-rose-700" })[v] || "";
}
